import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../../firebase/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import {
  FaHandshake,
  FaHandsHelping,
  FaCheckCircle,
  FaSpinner,
  FaPaperPlane,
} from 'react-icons/fa';
import './GetInvolved.css';

/* ── Partnership Types ── */
const PARTNERSHIP_TYPES = [
  'Sponsorship',
  'Research collaboration',
  'Event partnership',
  'Corporate membership',
  'Other',
];

/* ── Volunteer Interest Areas ── */
const INTEREST_AREAS = [
  'Events & logistics',
  'Outreach & education',
  'Content & social media',
  'Research support',
  'Administrative support',
];

const AVAILABILITY_OPTIONS = ['Weekdays', 'Weekends', 'Flexible'];

/* ── Empty States ── */
const EMPTY_PARTNER = {
  organisationName: '',
  contactName:      '',
  email:             '',
  phone:             '',
  partnershipType:   '',
  message:           '',
  honeypot:          '',
};

const EMPTY_VOLUNTEER = {
  firstName:    '',
  lastName:     '',
  email:        '',
  phone:        '',
  interests:    [],
  availability: '',
  message:      '',
  honeypot:     '',
};

/* ── Validation ── */
function validatePartner(f) {
  const errs = {};
  if (!f.organisationName.trim()) errs.organisationName = 'Organisation name is required.';
  if (!f.contactName.trim())      errs.contactName = 'Contact person name is required.';
  if (!f.email.trim())            errs.email = 'Email address is required.';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) errs.email = 'Enter a valid email address.';
  if (!f.partnershipType)         errs.partnershipType = 'Please select a partnership type.';
  if (!f.message.trim())          errs.message = 'Please tell us about your proposal.';
  else if (f.message.trim().length < 30) errs.message = 'Please write at least 30 characters.';
  return errs;
}

function validateVolunteer(f) {
  const errs = {};
  if (!f.firstName.trim()) errs.firstName = 'First name is required.';
  if (!f.lastName.trim())  errs.lastName = 'Last name is required.';
  if (!f.email.trim())     errs.email = 'Email address is required.';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) errs.email = 'Enter a valid email address.';
  if (f.interests.length === 0) errs.interests = 'Select at least one area of interest.';
  if (!f.availability)     errs.availability = 'Please select your availability.';
  if (!f.message.trim())   errs.message = 'Please tell us why you want to volunteer.';
  else if (f.message.trim().length < 30) errs.message = 'Please write at least 30 characters.';
  return errs;
}

/* ── Success State ── */
function SuccessState({ mode, onReset }) {
  const isPartner = mode === 'partner';
  return (
    <div className="gi-success" role="alert" aria-live="polite">
      <div className="gi-success__icon" aria-hidden="true">
        <FaCheckCircle size={42} />
      </div>
      <h2 className="gi-success__title">
        {isPartner ? 'Partnership Inquiry Sent' : 'Volunteer Application Sent'}
      </h2>
      <p className="gi-success__text">
        Thank you for your interest in {isPartner ? 'partnering with' : 'volunteering with'} NSK.
        Our team has received your submission and will get back to you at the
        email address you provided within a few business days.
        <br /><br />
        Questions in the meantime? Reach us at{' '}
        <strong>info@nuclearsocietyofkenya.org</strong>.
      </p>
      <div className="gi-success__actions">
        <Link to="/" className="btn btn--green">Back to Home</Link>
        <button
          className="btn btn--outline"
          onClick={onReset}
          style={{ color: 'var(--color-primary)', borderColor: 'var(--color-primary)' }}
        >
          Submit Another
        </button>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════
   GET INVOLVED PAGE
══════════════════════════════════════════ */
function GetInvolved() {
  const [mode, setMode] = useState(null); // 'partner' | 'volunteer' | null

  const [partnerFields, setPartnerFields] = useState(EMPTY_PARTNER);
  const [partnerErrors, setPartnerErrors] = useState({});
  const [partnerStatus, setPartnerStatus] = useState('idle');

  const [volunteerFields, setVolunteerFields] = useState(EMPTY_VOLUNTEER);
  const [volunteerErrors, setVolunteerErrors] = useState({});
  const [volunteerStatus, setVolunteerStatus] = useState('idle');

  /* ── Partner handlers ── */
  const handlePartnerChange = useCallback((e) => {
    const { name, value } = e.target;
    setPartnerFields(prev => ({ ...prev, [name]: value }));
    setPartnerErrors(prev => {
      if (!prev[name]) return prev;
      const n = { ...prev }; delete n[name]; return n;
    });
  }, []);

  const handlePartnerSubmit = useCallback(async () => {
    if (partnerFields.honeypot) return;
    const errs = validatePartner(partnerFields);
    if (Object.keys(errs).length > 0) {
      setPartnerErrors(errs);
      document.getElementById(`gp-${Object.keys(errs)[0]}`)?.focus();
      return;
    }
    setPartnerStatus('submitting');
    try {
      await addDoc(collection(db, 'partnershipInquiries'), {
        organisationName: partnerFields.organisationName,
        contactName:       partnerFields.contactName,
        email:              partnerFields.email,
        phone:              partnerFields.phone,
        partnershipType:    partnerFields.partnershipType,
        message:            partnerFields.message,
        createdAt:          serverTimestamp(),
      });
      setPartnerStatus('success');
    } catch (error) {
      console.error(error);
      alert('Failed to submit inquiry. Please try again.');
      setPartnerStatus('idle');
    }
  }, [partnerFields]);

  /* ── Volunteer handlers ── */
  const handleVolunteerChange = useCallback((e) => {
    const { name, value } = e.target;
    setVolunteerFields(prev => ({ ...prev, [name]: value }));
    setVolunteerErrors(prev => {
      if (!prev[name]) return prev;
      const n = { ...prev }; delete n[name]; return n;
    });
  }, []);

  const toggleInterest = useCallback((interest) => {
    setVolunteerFields(prev => {
      const has = prev.interests.includes(interest);
      return {
        ...prev,
        interests: has
          ? prev.interests.filter(i => i !== interest)
          : [...prev.interests, interest],
      };
    });
    setVolunteerErrors(prev => {
      if (!prev.interests) return prev;
      const n = { ...prev }; delete n.interests; return n;
    });
  }, []);

  const handleVolunteerSubmit = useCallback(async () => {
    if (volunteerFields.honeypot) return;
    const errs = validateVolunteer(volunteerFields);
    if (Object.keys(errs).length > 0) {
      setVolunteerErrors(errs);
      document.getElementById(`gv-${Object.keys(errs)[0]}`)?.focus();
      return;
    }
    setVolunteerStatus('submitting');
    try {
      await addDoc(collection(db, 'volunteerApplications'), {
        firstName:    volunteerFields.firstName,
        lastName:     volunteerFields.lastName,
        email:        volunteerFields.email,
        phone:        volunteerFields.phone,
        interests:    volunteerFields.interests,
        availability: volunteerFields.availability,
        message:      volunteerFields.message,
        createdAt:    serverTimestamp(),
      });
      setVolunteerStatus('success');
    } catch (error) {
      console.error(error);
      alert('Failed to submit application. Please try again.');
      setVolunteerStatus('idle');
    }
  }, [volunteerFields]);

  const resetAll = useCallback(() => {
    setMode(null);
    setPartnerFields(EMPTY_PARTNER);
    setPartnerErrors({});
    setPartnerStatus('idle');
    setVolunteerFields(EMPTY_VOLUNTEER);
    setVolunteerErrors({});
    setVolunteerStatus('idle');
  }, []);

  return (
    <>
      {/* ── Hero ── */}
      <section className="gi-hero" aria-labelledby="gi-hero-heading">
        <div className="gi-hero__inner">
          <nav className="gi-hero__breadcrumb" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span className="gi-hero__breadcrumb-sep" aria-hidden="true">›</span>
            <span>Get Involved</span>
          </nav>
          <h1 className="gi-hero__title" id="gi-hero-heading">Get Involved with NSK</h1>
          <p className="gi-hero__lead">
            Whether you represent an organisation looking to collaborate, or
            you're an individual who wants to contribute your time, there's a
            place for you at NSK.
          </p>
        </div>
      </section>

      {/* ── Path Selector ── */}
      <section className="gi-paths" aria-label="Choose how to get involved">
        <div className="gi-paths__inner">
          <button
            type="button"
            className={`gi-path-card${mode === 'partner' ? ' active' : ''}`}
            onClick={() => setMode('partner')}
            aria-pressed={mode === 'partner'}
          >
            <div className="gi-path-card__icon" aria-hidden="true">
              <FaHandshake size={28} />
            </div>
            <h2 className="gi-path-card__title">Partner with NSK</h2>
            <p className="gi-path-card__text">
              For organisations, institutions, and companies wanting to
              collaborate on research, sponsorship, or events.
            </p>
            <span className="gi-path-card__cta">Start a partnership inquiry →</span>
          </button>

          <button
            type="button"
            className={`gi-path-card${mode === 'volunteer' ? ' active' : ''}`}
            onClick={() => setMode('volunteer')}
            aria-pressed={mode === 'volunteer'}
          >
            <div className="gi-path-card__icon" aria-hidden="true">
              <FaHandsHelping size={28} />
            </div>
            <h2 className="gi-path-card__title">Volunteer with NSK</h2>
            <p className="gi-path-card__text">
              For individuals who want to give their time and skills to
              support events, outreach, and NSK's day-to-day work.
            </p>
            <span className="gi-path-card__cta">Apply to volunteer →</span>
          </button>
        </div>
      </section>

      {/* ── Partner Form ── */}
      {mode === 'partner' && (
        <section className="gi-form-section" aria-label="Partnership inquiry form">
          <div className="gi-form-card">
            {partnerStatus === 'success' ? (
              <SuccessState mode="partner" onReset={resetAll} />
            ) : (
              <>
                <h2 className="gi-form-card__heading">Partnership Inquiry</h2>
                <p className="gi-form-card__sub">
                  Fields marked <span style={{ color: 'var(--color-danger)' }}>*</span> are required.
                </p>

                <div className="giform" role="group" aria-label="Partnership inquiry form">
                  <div className="giform__honeypot" aria-hidden="true">
                    <label htmlFor="gp-website">Website</label>
                    <input
                      id="gp-website" name="honeypot" type="text"
                      value={partnerFields.honeypot} onChange={handlePartnerChange}
                      tabIndex={-1} autoComplete="off"
                    />
                  </div>

                  <div className="giform__row">
                    <div className="giform__field">
                      <label htmlFor="gp-organisationName" className="giform__label giform__label--required">
                        Organisation Name
                      </label>
                      <input
                        id="gp-organisationName" name="organisationName" type="text"
                        className={`giform__input${partnerErrors.organisationName ? ' error' : ''}`}
                        value={partnerFields.organisationName} onChange={handlePartnerChange}
                      />
                      {partnerErrors.organisationName && (
                        <span className="giform__error" role="alert">{partnerErrors.organisationName}</span>
                      )}
                    </div>
                    <div className="giform__field">
                      <label htmlFor="gp-contactName" className="giform__label giform__label--required">
                        Contact Person
                      </label>
                      <input
                        id="gp-contactName" name="contactName" type="text"
                        className={`giform__input${partnerErrors.contactName ? ' error' : ''}`}
                        value={partnerFields.contactName} onChange={handlePartnerChange}
                      />
                      {partnerErrors.contactName && (
                        <span className="giform__error" role="alert">{partnerErrors.contactName}</span>
                      )}
                    </div>
                  </div>

                  <div className="giform__row">
                    <div className="giform__field">
                      <label htmlFor="gp-email" className="giform__label giform__label--required">
                        Email Address
                      </label>
                      <input
                        id="gp-email" name="email" type="email"
                        className={`giform__input${partnerErrors.email ? ' error' : ''}`}
                        value={partnerFields.email} onChange={handlePartnerChange}
                      />
                      {partnerErrors.email && (
                        <span className="giform__error" role="alert">{partnerErrors.email}</span>
                      )}
                    </div>
                    <div className="giform__field">
                      <label htmlFor="gp-phone" className="giform__label">Phone Number</label>
                      <input
                        id="gp-phone" name="phone" type="tel"
                        className="giform__input"
                        value={partnerFields.phone} onChange={handlePartnerChange}
                        placeholder="+254 700 000 000"
                      />
                    </div>
                  </div>

                  <div className="giform__field">
                    <label htmlFor="gp-partnershipType" className="giform__label giform__label--required">
                      Type of Partnership
                    </label>
                    <select
                      id="gp-partnershipType" name="partnershipType"
                      className={`giform__input${partnerErrors.partnershipType ? ' error' : ''}`}
                      value={partnerFields.partnershipType} onChange={handlePartnerChange}
                    >
                      <option value="">Select a type</option>
                      {PARTNERSHIP_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                    {partnerErrors.partnershipType && (
                      <span className="giform__error" role="alert">{partnerErrors.partnershipType}</span>
                    )}
                  </div>

                  <div className="giform__field">
                    <label htmlFor="gp-message" className="giform__label giform__label--required">
                      Tell us about your proposal
                    </label>
                    <textarea
                      id="gp-message" name="message" rows={5}
                      className={`giform__textarea${partnerErrors.message ? ' error' : ''}`}
                      value={partnerFields.message} onChange={handlePartnerChange}
                      placeholder="What would this partnership involve, and what does your organisation bring to it?"
                    />
                    {partnerErrors.message && (
                      <span className="giform__error" role="alert">{partnerErrors.message}</span>
                    )}
                  </div>

                  <div className="giform__submit-row">
                    <p className="giform__privacy">
                      Your details are used only to respond to this inquiry.
                    </p>
                    <button
                      type="button" className="giform__submit"
                      onClick={handlePartnerSubmit}
                      disabled={partnerStatus === 'submitting'}
                      aria-busy={partnerStatus === 'submitting'}
                    >
                      {partnerStatus === 'submitting' ? (
                        <><FaSpinner size={14} style={{ animation: 'spin 1s linear infinite' }} /> Sending…</>
                      ) : (
                        <><FaPaperPlane size={14} /> Send Inquiry</>
                      )}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </section>
      )}

      {/* ── Volunteer Form ── */}
      {mode === 'volunteer' && (
        <section className="gi-form-section" aria-label="Volunteer application form">
          <div className="gi-form-card">
            {volunteerStatus === 'success' ? (
              <SuccessState mode="volunteer" onReset={resetAll} />
            ) : (
              <>
                <h2 className="gi-form-card__heading">Volunteer Application</h2>
                <p className="gi-form-card__sub">
                  Fields marked <span style={{ color: 'var(--color-danger)' }}>*</span> are required.
                </p>

                <div className="giform" role="group" aria-label="Volunteer application form">
                  <div className="giform__honeypot" aria-hidden="true">
                    <label htmlFor="gv-website">Website</label>
                    <input
                      id="gv-website" name="honeypot" type="text"
                      value={volunteerFields.honeypot} onChange={handleVolunteerChange}
                      tabIndex={-1} autoComplete="off"
                    />
                  </div>

                  <div className="giform__row">
                    <div className="giform__field">
                      <label htmlFor="gv-firstName" className="giform__label giform__label--required">
                        First Name
                      </label>
                      <input
                        id="gv-firstName" name="firstName" type="text"
                        className={`giform__input${volunteerErrors.firstName ? ' error' : ''}`}
                        value={volunteerFields.firstName} onChange={handleVolunteerChange}
                      />
                      {volunteerErrors.firstName && (
                        <span className="giform__error" role="alert">{volunteerErrors.firstName}</span>
                      )}
                    </div>
                    <div className="giform__field">
                      <label htmlFor="gv-lastName" className="giform__label giform__label--required">
                        Last Name
                      </label>
                      <input
                        id="gv-lastName" name="lastName" type="text"
                        className={`giform__input${volunteerErrors.lastName ? ' error' : ''}`}
                        value={volunteerFields.lastName} onChange={handleVolunteerChange}
                      />
                      {volunteerErrors.lastName && (
                        <span className="giform__error" role="alert">{volunteerErrors.lastName}</span>
                      )}
                    </div>
                  </div>

                  <div className="giform__row">
                    <div className="giform__field">
                      <label htmlFor="gv-email" className="giform__label giform__label--required">
                        Email Address
                      </label>
                      <input
                        id="gv-email" name="email" type="email"
                        className={`giform__input${volunteerErrors.email ? ' error' : ''}`}
                        value={volunteerFields.email} onChange={handleVolunteerChange}
                      />
                      {volunteerErrors.email && (
                        <span className="giform__error" role="alert">{volunteerErrors.email}</span>
                      )}
                    </div>
                    <div className="giform__field">
                      <label htmlFor="gv-phone" className="giform__label">Phone Number</label>
                      <input
                        id="gv-phone" name="phone" type="tel"
                        className="giform__input"
                        value={volunteerFields.phone} onChange={handleVolunteerChange}
                        placeholder="+254 700 000 000"
                      />
                    </div>
                  </div>

                  <div className="giform__field">
                    <span className="giform__label giform__label--required">
                      Areas of Interest
                    </span>
                    <div className="giform__checkbox-group" id="gv-interests">
                      {INTEREST_AREAS.map(area => (
                        <label className="giform__checkbox" key={area}>
                          <input
                            type="checkbox"
                            checked={volunteerFields.interests.includes(area)}
                            onChange={() => toggleInterest(area)}
                          />
                          {area}
                        </label>
                      ))}
                    </div>
                    {volunteerErrors.interests && (
                      <span className="giform__error" role="alert">{volunteerErrors.interests}</span>
                    )}
                  </div>

                  <div className="giform__field">
                    <label htmlFor="gv-availability" className="giform__label giform__label--required">
                      Availability
                    </label>
                    <select
                      id="gv-availability" name="availability"
                      className={`giform__input${volunteerErrors.availability ? ' error' : ''}`}
                      value={volunteerFields.availability} onChange={handleVolunteerChange}
                    >
                      <option value="">Select availability</option>
                      {AVAILABILITY_OPTIONS.map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                    {volunteerErrors.availability && (
                      <span className="giform__error" role="alert">{volunteerErrors.availability}</span>
                    )}
                  </div>

                  <div className="giform__field">
                    <label htmlFor="gv-message" className="giform__label giform__label--required">
                      Why do you want to volunteer with NSK?
                    </label>
                    <textarea
                      id="gv-message" name="message" rows={5}
                      className={`giform__textarea${volunteerErrors.message ? ' error' : ''}`}
                      value={volunteerFields.message} onChange={handleVolunteerChange}
                      placeholder="Tell us about your interest and what you'd like to help with…"
                    />
                    {volunteerErrors.message && (
                      <span className="giform__error" role="alert">{volunteerErrors.message}</span>
                    )}
                  </div>

                  <div className="giform__submit-row">
                    <p className="giform__privacy">
                      Your details are used only to process this application.
                    </p>
                    <button
                      type="button" className="giform__submit"
                      onClick={handleVolunteerSubmit}
                      disabled={volunteerStatus === 'submitting'}
                      aria-busy={volunteerStatus === 'submitting'}
                    >
                      {volunteerStatus === 'submitting' ? (
                        <><FaSpinner size={14} style={{ animation: 'spin 1s linear infinite' }} /> Sending…</>
                      ) : (
                        <><FaPaperPlane size={14} /> Submit Application</>
                      )}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </section>
      )}
    </>
  );
}

export default GetInvolved;